import { Link } from '@tanstack/react-router';
import { ArrowLeft, Shield, Eye, Database, Lock, UserCheck, Globe, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button, Card, CardHeader, CardTitle, CardContent } from '../../shared/ui';

/**
 * Privacy Policy Page
 * Public legal page accessible from landing footer and register form
 */
export function PrivacyPage() {
    const { t } = useTranslation();

    const sections = [
        {
            icon: <Eye className="h-5 w-5" />,
            titleKey: 'privacy.sections.dataCollection.title',
            itemKeys: [
                'privacy.sections.dataCollection.items.account',
                'privacy.sections.dataCollection.items.usage',
                'privacy.sections.dataCollection.items.sourceCode',
                'privacy.sections.dataCollection.items.technical',
            ],
        },
        {
            icon: <Database className="h-5 w-5" />,
            titleKey: 'privacy.sections.dataUsage.title',
            itemKeys: [
                'privacy.sections.dataUsage.items.sast',
                'privacy.sections.dataUsage.items.experience',
                'privacy.sections.dataUsage.items.notifications',
                'privacy.sections.dataUsage.items.reports',
                'privacy.sections.dataUsage.items.updates',
            ],
        },
        {
            icon: <Lock className="h-5 w-5" />,
            titleKey: 'privacy.sections.security.title',
            itemKeys: [
                'privacy.sections.security.items.encryption',
                'privacy.sections.security.items.jwt',
                'privacy.sections.security.items.passwords',
                'privacy.sections.security.items.serverAccess',
                'privacy.sections.security.items.audits',
            ],
        },
        {
            icon: <UserCheck className="h-5 w-5" />,
            titleKey: 'privacy.sections.rights.title',
            itemKeys: [
                'privacy.sections.rights.items.access',
                'privacy.sections.rights.items.rectification',
                'privacy.sections.rights.items.deletion',
                'privacy.sections.rights.items.portability',
                'privacy.sections.rights.items.opposition',
            ],
        },
        {
            icon: <Globe className="h-5 w-5" />,
            titleKey: 'privacy.sections.cookies.title',
            itemKeys: [
                'privacy.sections.cookies.items.essential',
                'privacy.sections.cookies.items.preferences',
                'privacy.sections.cookies.items.noTracking',
            ],
        },
    ];

    return (
        <div className="min-h-screen bg-background">
            {/* Header */}
            <header className="sticky top-0 z-50 border-b border-border bg-background px-8 py-4">
                <div className="mx-auto max-w-4xl flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/">
                            <Button variant="ghost" size="sm" corners="asymmetric" icon={<ArrowLeft className="h-4 w-4" />}>
                                {t('common.back')}
                            </Button>
                        </Link>
                    </div>
                    <div className="flex items-center gap-3">
                        <img src="/favicon-32x32.png" alt="DevLint" className="h-8 w-8" />
                        <span className="font-medium text-text-primary font-mono">DevLint</span>
                    </div>
                </div>
            </header>

            <main className="mx-auto max-w-4xl px-8 py-12">
                {/* Page Title */}
                <div className="mb-12 text-center">
                    <div className="inline-flex items-center justify-center h-16 w-16 rounded-asymmetric-lg bg-surface-hover border border-border mb-6">
                        <Shield className="h-8 w-8 text-primary" />
                    </div>
                    <h1 className="text-4xl font-bold text-text-primary font-mono tracking-tight">
                        {t('privacy.title')}
                    </h1>
                    <p className="mt-4 text-text-muted font-mono">
                        {t('privacy.lastUpdated')}
                    </p>
                </div>

                {/* Introduction */}
                <Card padding="lg" corners="asymmetric" className="mb-8">
                    <CardContent>
                        <p className="text-text-secondary leading-relaxed" dangerouslySetInnerHTML={{ __html: t('privacy.intro') }} />
                    </CardContent>
                </Card>

                {/* Sections */}
                <div className="space-y-6">
                    {sections.map((section, idx) => (
                        <Card key={idx} padding="lg" corners={idx % 2 === 0 ? 'asymmetric' : 'asymmetric-inverse'}>
                            <CardHeader>
                                <CardTitle className="flex items-center gap-3">
                                    <span className="inline-flex h-10 w-10 items-center justify-center rounded-asymmetric-md bg-surface-hover border border-border text-primary">
                                        {section.icon}
                                    </span>
                                    {t(section.titleKey)}
                                </CardTitle>
                            </CardHeader>
                            <CardContent>
                                <ul className="space-y-3">
                                    {section.itemKeys.map((key, i) => (
                                        <li key={i} className="flex items-start gap-3 text-text-secondary">
                                            <span className="mt-2 h-1.5 w-1.5 rounded-full bg-primary flex-shrink-0" />
                                            <span dangerouslySetInnerHTML={{ __html: t(key) }} />
                                        </li>
                                    ))}
                                </ul>
                            </CardContent>
                        </Card>
                    ))}
                </div>

                {/* Contact */}
                <Card padding="lg" corners="asymmetric" className="mt-8">
                    <CardContent>
                        <div className="flex items-center gap-3 mb-4">
                            <Mail className="h-5 w-5 text-primary" />
                            <h3 className="text-lg font-semibold text-text-primary font-mono">{t('privacy.contact.title')}</h3>
                        </div>
                        <p className="text-text-secondary" dangerouslySetInnerHTML={{ __html: t('privacy.contact.description') }} />
                    </CardContent>
                </Card>
            </main>

            {/* Footer */}
            <footer className="border-t border-border py-8">
                <div className="mx-auto max-w-4xl px-8">
                    <div className="flex flex-col items-center justify-between gap-4 md:flex-row">
                        <p className="text-sm text-text-muted font-mono">{t('landing.footer.copyright')}</p>
                        <div className="flex gap-6 text-sm text-text-muted font-mono">
                            <Link to="/terms" className="hover:text-text-primary transition-colors">{t('landing.footer.terms')}</Link>
                            <Link to="/contact" className="hover:text-text-primary transition-colors">{t('landing.footer.contact')}</Link>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
}
